// modules/BattleAnimation.js
import DebugHelper from './DebugHelper.js';

export default class BattleAnimation {
    constructor(scene) {
        this.scene = scene;
        this.animations = {};
    }

    create() {
        this.createAttackAnimations();
        this.createExplosionAnimations();
        this.createSpecialEffects();
    }

    createAttackAnimations() {
        // 炮弹发射动画
        if (!this.scene.anims.exists('fireBullet')) {
            this.scene.anims.create({
                key: 'fireBullet',
                frames: this.scene.anims.generateFrameNumbers('laser', { start: 0, end: 5 }),
                frameRate: 15,
                repeat: 0
            });
        }
        
        // 范围攻击动画
        if (!this.scene.anims.exists('multiAttack')) {
            this.scene.anims.create({
                key: 'multiAttack',
                frames: this.scene.anims.generateFrameNumbers('laser', { start: 0, end: 5 }),
                frameRate: 20,
                repeat: 0
            });
        }
    }

    createExplosionAnimations() {
        // 爆炸动画
        if (!this.scene.anims.exists('explode')) {
            this.scene.anims.create({
                key: 'explode',
                frames: this.scene.anims.generateFrameNumbers('laser', { start: 6, end: 14 }),
                frameRate: 12,
                repeat: 0
            });
        }
        
        // 多目标爆炸动画
        if (!this.scene.anims.exists('multiExplode')) {
            this.scene.anims.create({
                key: 'multiExplode',
                frames: this.scene.anims.generateFrameNumbers('laser', { start: 6, end: 14 }),
                frameRate: 12,
                repeat: 0
            });
        }
    }

    createSpecialEffects() {
        // 治疗动画
        if (!this.scene.anims.exists('heal')) {
            this.scene.anims.create({
                key: 'heal',
                frames: this.scene.anims.generateFrameNumbers('heal', { start: 0, end: 5 }),
                frameRate: 10,
                repeat: 0
            });
        }
        
        // 暴击动画
        if (!this.scene.anims.exists('critical')) {
            this.scene.anims.create({
                key: 'critical',
                frames: this.scene.anims.generateFrameNumbers('critical', { start: 0, end: 5 }),
                frameRate: 15,
                repeat: 0
            });
        }
    }

    playAttackAnimation(attacker, target, damage, onComplete, animationConfig = null) {
        const startX = attacker.x;
        const startY = attacker.y;
        const endX = target.x;
        const endY = target.y;
        
        // 获取动画配置，如果没有则使用默认值（向后兼容）
        const fireTexture = animationConfig?.fire || 'laser';
        const fireBulletFrames = animationConfig?.fireBullet || [0, 5];
        const explodeFrames = animationConfig?.explode || [6, 14];
        
        // 生成唯一的动画键名（基于纹理和帧范围）
        const fireAnimKey = `fire_${fireTexture}_${fireBulletFrames[0]}_${fireBulletFrames[1]}`;
        const explodeAnimKey = `explode_${fireTexture}_${explodeFrames[0]}_${explodeFrames[1]}`;
        
        this.createAnimationIfNotExists(fireAnimKey, fireTexture, fireBulletFrames[0], fireBulletFrames[1], 15);
        this.createAnimationIfNotExists(explodeAnimKey, fireTexture, explodeFrames[0], explodeFrames[1], 30);
        
        // 创建炮弹
        const bullet = this.scene.add.sprite(startX, startY, fireTexture);
        bullet.setScale(1.2);
        bullet.setOrigin(0.5, 0.5);
        bullet.play(fireAnimKey);
        
        // 飞行动画
        this.scene.tweens.add({
            targets: bullet,
            x: endX,
            y: endY,
            duration: 500,
            ease: 'Linear',
            onComplete: () => {
                bullet.destroy();
                this.playExplosionEffect(endX, endY, fireTexture, explodeAnimKey, target, damage, onComplete, 1.5, 1.1, 200, false);
            }
        });
    }

    /**
     * 播放默认攻击动画（无武器时使用）
     * 角色抖动 -> 敌人抖动 -> 显示伤害
     */
    playDefaultAttackAnimation(attacker, target, damage, onComplete) {
        // 保存原始位置
        const attackerOriginalX = attacker.x;
        const attackerOriginalY = attacker.y;
        const targetOriginalX = target.x;
        const targetOriginalY = target.y;
        
        // 第一步：角色抖动（攻击动作）
        this.scene.tweens.add({
            targets: attacker,
            x: { from: attackerOriginalX, to: attackerOriginalX + 5 },
            y: { from: attackerOriginalY, to: attackerOriginalY - 3 },
            duration: 100,
            ease: 'Power2',
            yoyo: true,
            repeat: 1,
            onComplete: () => {
                // 角色回到原位
                this.scene.tweens.add({
                    targets: attacker,
                    x: attackerOriginalX,
                    y: attackerOriginalY,
                    duration: 50,
                    ease: 'Power2',
                    onComplete: () => {
                        // 第二步：敌人抖动（被击中）
                        this.scene.tweens.add({
                            targets: target,
                            x: { from: targetOriginalX, to: targetOriginalX - 8 },
                            y: { from: targetOriginalY, to: targetOriginalY + 2 },
                            duration: 150,
                            ease: 'Power2',
                            yoyo: true,
                            repeat: 1,
                            onComplete: () => {
                                // 敌人回到原位
                                this.scene.tweens.add({
                                    targets: target,
                                    x: targetOriginalX,
                                    y: targetOriginalY,
                                    duration: 50,
                                    ease: 'Power2',
                                    onComplete: () => {
                                        // 显示伤害数字
                                        this.showDamage(target, damage);
                                        
                                        // 回调
                                        if (onComplete) onComplete();
                                    }
                                });
                            }
                        });
                    }
                });
            }
        });
    }

    playRangeAttackAnimation(attacker, target, damage, onComplete, animationConfig = null) {
        const startX = attacker.x;
        const startY = attacker.y;
        const endX = target.x;
        const endY = target.y;
        
        // 获取动画配置，如果没有则使用默认值（向后兼容）
        const fireTexture = animationConfig?.fire || 'laser';
        const fireBulletFrames = animationConfig?.fireBullet || [0, 5];
        const explodeFrames = animationConfig?.explode || [6, 14];
        
        // 生成唯一的动画键名（基于纹理和帧范围）
        const fireAnimKey = `range_fire_${fireTexture}_${fireBulletFrames[0]}_${fireBulletFrames[1]}`;
        const explodeAnimKey = `range_explode_${fireTexture}_${explodeFrames[0]}_${explodeFrames[1]}`;
        
        this.createAnimationIfNotExists(fireAnimKey, fireTexture, fireBulletFrames[0], fireBulletFrames[1], 20);
        this.createAnimationIfNotExists(explodeAnimKey, fireTexture, explodeFrames[0], explodeFrames[1], 30);
        
        // 创建炮弹（范围攻击使用不同颜色）
        const bullet = this.scene.add.sprite(startX, startY, fireTexture);
        bullet.setScale(1.5);
        bullet.setTint(0xffaa00); // 橙色
        bullet.setOrigin(0.5, 0.5);
        bullet.play(fireAnimKey);
        
        // 飞行动画
        this.scene.tweens.add({
            targets: bullet,
            x: endX,
            y: endY,
            duration: 1000,
            ease: 'Power2',
            onComplete: () => {
                bullet.destroy();
                this.playExplosionEffect(endX, endY, fireTexture, explodeAnimKey, target, damage, onComplete, 1.3, 1.2, 300, true, 0x00ff00);
            }
        });
    }

    playEnemyAttackAnimation(enemy, target, damage, onComplete) {
        if (!enemy || !target || !target.active) {
            DebugHelper.warn('BattleAnimation: 敌人攻击动画参数无效', { enemy: !!enemy, target: !!target });
            if (onComplete) onComplete();
            return;
        }
        
        const startX = enemy.x;
        const startY = enemy.y;
        const endX = target.x;
        const endY = target.y;
        
        // 创建敌人攻击效果
        let attackEffect;
        try {
            if (this.scene.textures.exists('bullet')) {
                attackEffect = this.scene.add.sprite(startX, startY, 'bullet');
            } else {
                // 如果纹理不存在，创建一个简单的图形
                attackEffect = this.scene.add.circle(startX, startY, 5, 0xff0000);
            }
            attackEffect.setScale(1.0);
            if (attackEffect.setTint) {
                attackEffect.setTint(0xff0000); // 红色表示敌人攻击
            }
            attackEffect.setOrigin(0.5, 0.5);
        } catch (error) {
            DebugHelper.error('BattleAnimation: 创建攻击效果失败', error);
            // 如果创建失败，直接执行回调
            if (onComplete) onComplete();
            return;
        }
        
        // 飞行动画
        this.scene.tweens.add({
            targets: attackEffect,
            x: endX,
            y: endY,
            duration: 400,
            ease: 'Linear',
            onComplete: () => {
                if (attackEffect && attackEffect.destroy) {
                    attackEffect.destroy();
                }
                
                // 检查目标是否仍然有效
                if (!target || !target.active) {
                    DebugHelper.warn('BattleAnimation: 目标已失效，跳过击中动画');
                    if (onComplete) onComplete();
                    return;
                }
                
                // 目标被击中动画
                this.scene.tweens.add({
                    targets: target,
                    scale: { from: target.scale, to: target.scale * 1.1 },
                    duration: 200,
                    yoyo: true,
                    onComplete: () => {
                        // 显示伤害数字
                        if (target && target.active) {
                            this.showDamage(target, damage, false, true); // 敌人攻击
                        }
                        
                        // 回调
                        if (onComplete) {
                            try {
                                onComplete();
                            } catch (error) {
                                DebugHelper.error('BattleAnimation: 攻击动画回调执行失败', error);
                            }
                        }
                    }
                });
            }
        });
    }

    /**
     * 播放敌人受击抖动动画
     * @param {Phaser.GameObjects.Image} enemySprite - 敌人精灵
     * @param {number} damage - 伤害值（用于判断是否需要抖动）
     * @param {Function} onComplete - 动画完成回调（可选）
     */
    playEnemyHitShake(enemySprite, damage, onComplete = null) {
        // 只有伤害大于0时才抖动
        const actualDamage = damage && typeof damage === 'object' ? damage.damage : damage;
        if (!actualDamage || actualDamage <= 0) {
            // 如果没有伤害，立即调用回调（如果有）
            if (onComplete) onComplete();
            return;
        }
        
        // 保存原始位置
        const originalX = enemySprite.x;
        const originalY = enemySprite.y;
        
        // 敌人受击抖动（向左下抖动，模拟被击退）
        this.scene.tweens.add({
            targets: enemySprite,
            x: { from: originalX, to: originalX - 8 },
            y: { from: originalY, to: originalY + 2 },
            duration: 150, // 增加抖动持续时间（从100ms增加到150ms）
            ease: 'Power2',
            yoyo: true,
            repeat: 1,
            onComplete: () => {
                // 确保回到原位
                this.scene.tweens.add({
                    targets: enemySprite,
                    x: originalX,
                    y: originalY,
                    duration: 100, // 增加回到原位的时间（从50ms增加到100ms）
                    ease: 'Power2',
                    onComplete: () => {
                        // 抖动完全完成后，再延迟一段时间再调用回调，让玩家有更多时间看到反馈
                        this.scene.time.delayedCall(500, () => {
                            if (onComplete) onComplete();
                        });
                    }
                });
            }
        });
    }

    showDamage(target, damage, isRangeAttack = false, isEnemyAttack = false) {
        let color = '#ffffff'; // 默认白色
        damage = damage.damage || damage; // 兼容两种返回值
        
        if (isRangeAttack) {
            color = '#ffff00'; // 范围攻击黄色
        } else if (isEnemyAttack) {
            color = '#ff6666'; // 敌人攻击浅红色
        }
        
        const damageText = this.scene.add.text(
            target.x,
            target.y - 20,
            damage.toString(),
            {
                fontSize: '24px',
                fill: color,
                fontFamily: 'monospace',
                fontWeight: 'bold',
                stroke: '#000000',
                strokeThickness: 3
            }
        ).setOrigin(0.5, 0.5);
        
        // 伤害数字动画
        this.scene.tweens.add({
            targets: damageText,
            y: target.y - 60,
            scale: { from: 1, to: 1.5 },
            alpha: { from: 1, to: 0 },
            duration: 1000,
            ease: 'Power2',
            onComplete: () => {
                damageText.destroy();
            }
        });
    }

    playHealAnimation(target, healAmount, onComplete) {
        // 治疗动画
        const healEffect = this.scene.add.sprite(target.x, target.y, 'heal');
        healEffect.setScale(1.0);
        healEffect.setTint(0x00ff00); // 绿色
        healEffect.setOrigin(0.5, 0.5);
        healEffect.play('heal');
        
        // 显示治疗数字
        const healText = this.scene.add.text(
            target.x,
            target.y - 20,
            `+${healAmount}`,
            {
                fontSize: '20px',
                fill: '#00ff00',
                fontFamily: 'monospace',
                fontWeight: 'bold',
                stroke: '#000000',
                strokeThickness: 3
            }
        ).setOrigin(0.5, 0.5);
        
        // 动画
        this.scene.tweens.add({
            targets: [healEffect, healText],
            y: target.y - 40,
            alpha: { from: 1, to: 0 },
            duration: 800,
            ease: 'Power2',
            onComplete: () => {
                healEffect.destroy();
                healText.destroy();
                if (onComplete) onComplete();
            }
        });
    }

    playCriticalAnimation(target, onComplete) {
        // 暴击动画
        const criticalEffect = this.scene.add.sprite(target.x, target.y, 'critical');
        criticalEffect.setScale(1.5);
        criticalEffect.setOrigin(0.5, 0.5);
        criticalEffect.play('critical');
        
        this.scene.time.delayedCall(500, () => {
            criticalEffect.destroy();
            if (onComplete) onComplete();
        });
    }

    playVictoryAnimation(onComplete) {
        // 胜利动画
        const centerX = this.scene.cameras.main.centerX;
        const centerY = this.scene.cameras.main.centerY;
        
        const victoryText = this.scene.add.text(
            centerX,
            centerY,
            'VICTORY!',
            {
                fontSize: '48px',
                fill: '#ffff00',
                fontFamily: 'monospace',
                fontWeight: 'bold',
                stroke: '#000000',
                strokeThickness: 6
            }
        ).setOrigin(0.5, 0.5);
        
        // 闪烁效果
        this.scene.tweens.add({
            targets: victoryText,
            scale: { from: 1, to: 1.2 },
            alpha: { from: 1, to: 0.7 },
            duration: 500,
            yoyo: true,
            repeat: 3,
            onComplete: () => {
                victoryText.destroy();
                if (onComplete) onComplete();
            }
        });
    }

    playDefeatAnimation(onComplete) {
        // 失败动画
        const centerX = this.scene.cameras.main.centerX;
        const centerY = this.scene.cameras.main.centerY;
        
        const defeatText = this.scene.add.text(
            centerX,
            centerY,
            'DEFEAT',
            {
                fontSize: '48px',
                fill: '#ff0000',
                fontFamily: 'monospace',
                fontWeight: 'bold',
                stroke: '#000000',
                strokeThickness: 6
            }
        ).setOrigin(0.5, 0.5);
        
        this.scene.time.delayedCall(2000, () => {
            defeatText.destroy();
            if (onComplete) onComplete();
        });
    }

    /**
     * 播放防御动画
     * @param {Phaser.GameObjects.Sprite} target - 防御目标
     * @param {Function} onComplete - 完成回调
     */
    playDefenseAnimation(target, onComplete = null) {
        // 防御效果：角色周围出现防护罩
        const shield = this.scene.add.circle(target.x, target.y, 30, 0x00ffff, 0.5);
        shield.setStrokeStyle(3, 0x00ffff);
        
        // 闪烁效果
        this.scene.tweens.add({
            targets: shield,
            scale: { from: 0.8, to: 1.2 },
            alpha: { from: 0.3, to: 0.7 },
            duration: 500,
            yoyo: true,
            repeat: 1,
            onComplete: () => {
                shield.destroy();
                if (onComplete) onComplete();
            }
        });
    }
    
    /**
     * 播放上车动画
     * @param {Phaser.GameObjects.Sprite} playerSprite - 玩家精灵
     * @param {Function} onComplete - 完成回调
     */
    playBoardVehicleAnimation(playerSprite, onComplete = null) {
        this.playFadeInOutAnimation(playerSprite, onComplete);
    }
    
    /**
     * 播放下车动画
     * @param {Phaser.GameObjects.Sprite} playerSprite - 玩家精灵
     * @param {Function} onComplete - 完成回调
     */
    playExitVehicleAnimation(playerSprite, onComplete = null) {
        this.playFadeInOutAnimation(playerSprite, onComplete);
    }
    
    update(time, delta) {
        // 动画更新逻辑（如果需要）
    }

    /**
     * 创建动画（如果不存在）
     * @param {string} animKey - 动画键名
     * @param {string} texture - 纹理键名
     * @param {number} startFrame - 起始帧
     * @param {number} endFrame - 结束帧
     * @param {number} frameRate - 帧率
     */
    createAnimationIfNotExists(animKey, texture, startFrame, endFrame, frameRate) {
        if (!this.scene.anims.exists(animKey)) {
            this.scene.anims.create({
                key: animKey,
                frames: this.scene.anims.generateFrameNumbers(texture, { 
                    start: startFrame, 
                    end: endFrame 
                }),
                frameRate: frameRate,
                repeat: 0
            });
        }
    }
    
    /**
     * 播放爆炸效果
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @param {string} texture - 纹理键名
     * @param {string} animKey - 动画键名
     * @param {Phaser.GameObjects.Sprite} target - 目标对象
     * @param {number} damage - 伤害值
     * @param {Function} onComplete - 完成回调
     * @param {number} explosionScale - 爆炸缩放
     * @param {number} targetScale - 目标缩放
     * @param {number} duration - 持续时间
     * @param {boolean} isRangeAttack - 是否范围攻击
     * @param {number} tint - 颜色（可选）
     */
    playExplosionEffect(x, y, texture, animKey, target, damage, onComplete, explosionScale, targetScale, duration, isRangeAttack, tint = null) {
        const explosion = this.scene.add.sprite(x, y, texture);
        explosion.setScale(explosionScale);
        if (tint !== null) {
            explosion.setTint(tint);
        }
        explosion.setOrigin(0.5, 0.5);
        explosion.play(animKey);
        
        this.scene.tweens.add({
            targets: target,
            scale: { from: target.scale, to: target.scale * targetScale },
            duration: duration,
            yoyo: true,
            onComplete: () => {
                explosion.destroy();
                this.showDamage(target, damage, isRangeAttack);
                if (onComplete) onComplete();
            }
        });
    }
    
    /**
     * 播放淡入淡出动画
     * @param {Phaser.GameObjects.Sprite} sprite - 精灵对象
     * @param {Function} onComplete - 完成回调
     */
    playFadeInOutAnimation(sprite, onComplete) {
        this.scene.tweens.add({
            targets: sprite,
            alpha: { from: 1, to: 0.3 },
            scale: { from: 1, to: 0.9 },
            duration: 300,
            onComplete: () => {
                this.scene.tweens.add({
                    targets: sprite,
                    alpha: { from: 0.3, to: 1 },
                    scale: { from: 0.9, to: 1 },
                    duration: 300,
                    onComplete: () => {
                        if (onComplete) onComplete();
                    }
                });
            }
        });
    }
    
    destroy() {
        // 清理动画
        if (this.scene.anims.exists('fireBullet')) {
            this.scene.anims.remove('fireBullet');
        }
        if (this.scene.anims.exists('explode')) {
            this.scene.anims.remove('explode');
        }
        if (this.scene.anims.exists('multiAttack')) {
            this.scene.anims.remove('multiAttack');
        }
        if (this.scene.anims.exists('multiExplode')) {
            this.scene.anims.remove('multiExplode');
        }
        if (this.scene.anims.exists('heal')) {
            this.scene.anims.remove('heal');
        }
        if (this.scene.anims.exists('critical')) {
            this.scene.anims.remove('critical');
        }
    }
}